import type { DrawerProps } from '@workramp/ui';
import {
  Button,
  CheckboxGroup,
  CheckboxGroupItem,
  Col,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  Row,
  Stack,
  TextField,
} from '@workramp/ui';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { isValidUrl } from '~/common';
import { I18n } from '~/common/explicit-exports';
import StringUtils from '~/common/string-utils';
import { ConfirmModal } from '~/features/common';

export interface WebhookSubscriptionDrawerProps extends Omit<DrawerProps, 'children'> {
  modalTitle: string;
  confirm?: string;
  cancel?: string;
  webhookEventTypeOptions: string[];
  deprecatedEventTypes?: string[];
  onClose: () => void;
  onSave: (data: { name: string; eventTypes: string[]; url: string }) => void;
  isOpen: boolean;
  name: string;
  url: string;
  eventTypes: string[];
}

export const WebhookSubscriptionDrawer = ({
  modalTitle,
  confirm,
  cancel,
  webhookEventTypeOptions,
  deprecatedEventTypes,
  name: initialName,
  url: initialUrl,
  eventTypes: intitialEventTypes,
  onClose,
  onSave,
  ...props
}: WebhookSubscriptionDrawerProps) => {
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, dirtyFields },
  } = useForm({
    shouldUnregister: true,
    defaultValues: {
      name: initialName,
      url: initialUrl,
      eventTypes: intitialEventTypes,
    },
  });

  const CANCEL = cancel || I18n.t('js.common.modal.cancel');
  const CONFIRM = confirm || I18n.t('js.common.modal.confirm');

  const NAME_FIELD = I18n.t('js.admin.integration_settings.webhook_subscriptions.drawer.name_field');
  const URL_FIELD = I18n.t('js.admin.integration_settings.webhook_subscriptions.drawer.url_field');
  const EVENTS_FIELD = I18n.t('js.admin.integration_settings.webhook_subscriptions.drawer.events_field');
  const BLANK_FIELD = I18n.t('js.admin.integration_settings.webhook_subscriptions.drawer.blank_field');
  const NAME_FIELD_PLACEHOLDER = I18n.t('js.admin.integration_settings.webhook_subscriptions.drawer.name_placeholder');
  const INVALID_URL = I18n.t('js.admin.integration_settings.webhook_subscriptions.drawer.invalid_url');
  const UNSAVED_CHANGES_MODAL_TITLE = I18n.t('js.admin.integration_settings.webhook_subscriptions.discard_modal.title');
  const UNSAVED_CHANGES_MODAL_DESCRIPTION = I18n.t(
    'js.admin.integration_settings.webhook_subscriptions.discard_modal.message',
  );
  const UNSAVED_CHANGES_MODAL_DISCARD = I18n.t(
    'js.admin.integration_settings.webhook_subscriptions.discard_modal.discard',
  );

  const handleEntering = () => {
    reset({
      name: initialName,
      url: initialUrl,
      eventTypes: intitialEventTypes,
    });
  };

  const handleExiting = () => {
    reset({
      name: '',
      url: '',
      eventTypes: [],
    });
  };

  const handleClose = () => {
    if (formIsDirty()) {
      setIsCancelModalOpen(true);
      return;
    }

    onClose();
  };

  const formIsDirty = () => Object.keys(dirtyFields).length > 0;

  const handleFormSubmit = handleSubmit((data) => onSave(data));

  return (
    <>
      <Drawer onClose={handleClose} {...props} onExited={handleExiting} onEntering={handleEntering}>
        <DrawerHeader title={modalTitle} />
        <DrawerBody>
          <form onSubmit={handleFormSubmit}>
            <Stack size={4}>
              <Controller
                control={control}
                name="name"
                rules={{
                  required: {
                    value: true,
                    message: BLANK_FIELD,
                  },
                }}
                render={({ field }) => (
                  <TextField
                    label={NAME_FIELD}
                    placeholder={NAME_FIELD_PLACEHOLDER}
                    {...field}
                    hasError={!!errors.name}
                    errorMessage={errors.name?.message}
                  />
                )}
              />
              <Controller
                control={control}
                name="url"
                rules={{
                  required: {
                    value: true,
                    message: BLANK_FIELD,
                  },
                  validate: {
                    isValidUrl: (v) => isValidUrl(v) || INVALID_URL,
                  },
                }}
                render={({ field }) => (
                  <TextField
                    label={URL_FIELD}
                    {...field}
                    placeholder="https://example.com/abc"
                    hasError={!!errors.url}
                    errorMessage={errors.url?.message}
                  />
                )}
              />
              <Controller
                control={control}
                name="eventTypes"
                rules={{
                  required: {
                    value: true,
                    message: BLANK_FIELD,
                  },
                }}
                render={({ field }) => (
                  <CheckboxGroup
                    label={EVENTS_FIELD}
                    {...field}
                    hasError={!!errors.eventTypes}
                    errorMessage={errors.eventTypes?.message}
                  >
                    {webhookEventTypeOptions.map((type) => (
                      <CheckboxGroupItem key={type} value={type}>
                        {StringUtils.getCapitalizedWordsFromCamelCasedString(type.replace('.', ': '))}
                        {deprecatedEventTypes?.includes(type) && <strong> [DEPRECATED]</strong>}
                      </CheckboxGroupItem>
                    ))}
                  </CheckboxGroup>
                )}
              />
            </Stack>
          </form>
        </DrawerBody>
        <DrawerFooter>
          <Row justify="flex-end" gapX={2}>
            <Col xs="auto">
              <Button color="secondary" size="lg" variant="text" onPress={handleClose} autoFocus>
                {CANCEL}
              </Button>
            </Col>
            <Col xs="auto">
              <Button onPress={() => handleFormSubmit()} size="lg" isDisabled={!formIsDirty()}>
                {CONFIRM}
              </Button>
            </Col>
          </Row>
        </DrawerFooter>
      </Drawer>
      <ConfirmModal
        isOpen={isCancelModalOpen}
        title={UNSAVED_CHANGES_MODAL_TITLE}
        body={UNSAVED_CHANGES_MODAL_DESCRIPTION}
        confirm={UNSAVED_CHANGES_MODAL_DISCARD}
        onConfirm={() => {
          setIsCancelModalOpen(false);
          onClose();
        }}
        onCancel={() => setIsCancelModalOpen(false)}
      />
    </>
  );
};
